/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, ErrorText, Field, Input, PageHeader, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtMoney } from '@/lib/format';

const STRAT: Record<string, string> = { PRETRIAL: 'Досудебно', SETTLEMENT: 'Мировое / рассрочка', COURT: 'Суд', BANKRUPTCY: 'Банкротство', CESSION: 'Цессия', WRITEOFF: 'Списание' };

/** M19 оценка портфеля и M23 отчёт по работе операторов с должниками */
export function CollectionPage() {
  const { hasModule } = useAuth();
  return (
    <>
      <PageHeader title="Аналитика взыскания" subtitle="Ожидаемые поступления по портфелю, работа операторов, проблемные диалоги" />
      <div className="space-y-5">
        {hasModule('M19') && <Portfolio />}
        {hasModule('M23') && <Report />}
      </div>
    </>
  );
}

function Portfolio() {
  const [pf, setPf] = useState('');
  const run = useMutation({ mutationFn: () => api.post<any>(`/scoring/portfolio${pf ? `?portfolio=${encodeURIComponent(pf)}` : ''}`, {}) });
  const r = run.data;
  return (
    <div className="card space-y-3 p-4">
      <div className="text-sm font-medium">Оценка портфеля долгов</div>
      <div className="flex flex-wrap gap-2"><Input className="w-64" placeholder="Реестр (пусто - все открытые долги)" value={pf} onChange={(e) => setPf(e.target.value)} /><Button disabled={run.isPending} onClick={() => run.mutate()}>{run.isPending ? 'Оцениваю...' : 'Оценить'}</Button></div>
      <ErrorText error={run.error} />
      {r && <div className="grid gap-3 sm:grid-cols-3">
        <div><div className="text-xs text-slate-500">Долгов</div><div className="text-lg font-semibold">{r.count}</div></div>
        <div><div className="text-xs text-slate-500">Сумма к взысканию</div><div className="text-lg font-semibold">{fmtMoney(r.total)}</div></div>
        <div><div className="text-xs text-slate-500">Ожидаемые поступления</div><div className="text-lg font-semibold text-brand-700">{fmtMoney(r.expected)}</div></div>
        <div className="flex flex-wrap gap-2 sm:col-span-3">{Object.entries(r.byStrategy).map(([k, n]) => <Badge key={k}>{STRAT[k] ?? k}: {String(n)}</Badge>)}</div>
      </div>}
    </div>
  );
}

function Report() {
  const [from, setFrom] = useState(new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const { data, isLoading } = useQuery({ queryKey: ['coll-report', from, to], queryFn: () => api.get<any>(`/collection/report?from=${from}&to=${to}`) });
  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-end gap-3"><div className="text-sm font-medium">Работа операторов</div><Field label="С"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field><Field label="По"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field></div>
      {isLoading ? <Spinner /> : data && <>
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr><th className="th">Оператор</th><th className="th text-right">Контактов</th><th className="th text-right">С нарушениями</th><th className="th text-right">Обещаний</th><th className="th text-right">Исполнено</th><th className="th text-right">Скрипт, %</th></tr></thead>
          <tbody>{data.operators.map((o: any) => <tr key={o.full_name} className="border-t border-slate-100"><td className="td">{o.full_name}</td><td className="td text-right">{o.contacts}</td><td className={`td text-right ${o.with_violations ? 'text-red-700' : ''}`}>{o.with_violations}</td><td className="td text-right">{o.promises}</td><td className="td text-right">{o.kept}</td><td className="td text-right">{o.script ?? '-'}</td></tr>)}</tbody>
        </table></div>
        {!data.operators.length && <div className="text-sm text-slate-500">За период контактов нет</div>}
        {!!data.problems.length && <div>
          <div className="mb-1 text-sm font-medium">Проблемные диалоги</div>
          <ul className="divide-y divide-slate-100 text-sm">{data.problems.map((p: any) => (
            <li key={p.id} className="py-1.5"><Link className="text-brand-700 hover:underline" to={`/debts/${p.debt_id}`}>{p.debtor}</Link> <span className="text-slate-500">{new Date(p.at).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })}, {p.operator}</span>{p.sentiment && <Badge tone="red">{p.sentiment}</Badge>}{(p.violations ?? []).slice(0, 2).map((v: any, i: number) => <div key={i} className="text-xs text-red-700">{v.text ?? v}</div>)}</li>
          ))}</ul>
        </div>}
      </>}
    </div>
  );
}
